import { Component, Inject, ViewChild } from '@angular/core';
import { IonMenu, ModalController, Platform, ToastController } from '@ionic/angular';
import { DOCUMENT } from '@angular/common';
import { Event, NavigationStart } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { register } from 'swiper/element/bundle';
import { AuthService } from './_services/auth.service';
import { EventsService } from './_services/events.service';
import { environment } from '../environments/environment';
import moment from 'moment';
import { GuidedVisitModalComponent } from './_components/guided-visit-modal/guided-visit-modal.component';
import { TutorialService } from './_services/tutorial.service';
import { ComponentBase } from './_utils/component.base';
import { ModuleEnum } from './_utils/unums/module.enum';
import { SettingsService } from './_services/settings.service';
import { MENU_SETTINGS } from './_utils/settings/menu.settings';

register();

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent extends ComponentBase {
  @ViewChild('mainMenu', {static: true})
  protected mainMenu: IonMenu;
  url;
  user;
  showMenu = false;

  features;
  version = environment.version;

  routerSubs;

  msg1 = 'is now listening to the context of the simulation and will soon be joining the simulation.';
  msg2 = 'your peer to the practice, has just logged in.';
  msg3 = 'is waiting for you to start the simulation.';

  public testPages = [];
  public appPages: any[];

  guideVisit;
  returnUrl;
  protected userJourney: any;
  private availableModules: ModuleEnum[];

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    private platform: Platform,
    private toastController: ToastController,
    public authService: AuthService,
    public translate: TranslateService,
    private events: EventsService,
    private modalController: ModalController,
    private tutorialService: TutorialService,
    private settingsService: SettingsService) {

    super();
    this.initVariables();
    this.listenAuthState();
    this.platform.ready().then(async () => {
      this.setFavicon();
      this.initApp();
    });

    this.router.routeReuseStrategy.shouldReuseRoute = () => false; // So the simulation pages code (like roleplay.ts) will reload and not save last state

    this.routerSubs = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this.url = event.url;
        if (['/', '/login', '/not-found'].includes(event.url))
          this.showMenu = false; // Show/Hide left-side menu
        else this.showMenu = true;
      }
    });

    this.tutorialService.initGuideVisitHandler()

    // Update user data after login
    // Open menu sub items according the current page the user is in
    this.events.getObservable().subscribe((data) => {
      if (data.user) {
        // After login
        this.user = data.user;

        this.setLanguage();
        this.validateJourneyDemo();
        this.getP2PSchedule();
      } else if (data.menu) {
        const page = this.appPages.find((p: any) => p.module === this.getSelectedModule());
        if (page) page.showItems = true;
        if (data.submenu && page.items) {
          let subpage = page.items.find((p) => p.name == data.submenu);
          if (subpage) subpage.available = true;
        }
      } else if (data.refreshsimulations) {
        // when clicking the refresh button in /simulations-peers
        this.getP2PSchedule();
      }
    });
  }

  private initVariables(): void {
    this.availableModules = [ModuleEnum.feedback, ModuleEnum.conversations, ModuleEnum.motivation, ModuleEnum.interviews, ModuleEnum.salesAndServices];
    this.appPages = MENU_SETTINGS;
    this.initMenuListener();
    this.initActiveMenuListener();

    // Show new features to test in dev
    this.features = environment.features ? `<ul>${environment.features.map(item => `<li> - ${item}</li>`).join('')}</ul>` : null;
  }

  private setFavicon() {
    if (environment.name != "production") {
      let link: HTMLLinkElement = this.doc.createElement('link');
      link.setAttribute('rel', 'icon');
      link.setAttribute('href', `assets/icon/favicon${environment.name == "development" ? '_dev' : environment.name == "staging" ? '_staging' : ''}.png`);
      this.doc.head.appendChild(link);
    }
  }

  async initApp() {
    let self = this;
    this.user = await this.authService.getUser();

    this.guideVisit = await this.storage.get('guided_visit');

    if (this.url.split('?')[0] === '/video') {
      return this.router.navigateByUrl(this.url);
    }

    // User is logged in
    if (this.user) {
      if (!this.user.firstName || !this.user.lastName || !this.user.gender) {
        this.router.navigateByUrl('/login');
        return;
      }
      // Check for coming P2P simulations and subscribe to peers actions
      this.getP2PSchedule();
      this.validateJourneyDemo();
    }
    // User in NOT logged in
    else {
      this.returnUrl = true;
      if (self.url !== '/' && !self.url.includes('/login')) {
        this.router.navigate(['/login'], {queryParamsHandling: 'merge', queryParams: {returnUrl: self.url}});
      }
    }
    this.setLanguage();

  }

  ngOnDestroy() {
    if (this.routerSubs) {
      this.routerSubs.unsubscribe();
    }
  }

  async setLanguage() {
    // Get the user's language: If it's the login page -> Get from query param. If not login page -> Get from localStorage
    // If no language found, get from saved value OR navigator's deafault language.
    let langParam = this.url.includes('/login') ? (new URL(window.location.href)).searchParams.get("lng") : await this.storage.get('lang');
    let lang = this.user && this.user.language ? this.user.language : langParam || navigator.language || 'es-ES';
    if (lang == 'es') lang = "es-ES";
    if (lang == 'en') lang = "en-US";

    let shortLang = lang.split('-')[0];
    moment.locale(shortLang);
    this.translate.use(shortLang);
    this.storage.set('lang', lang);
  }

  getP2PSchedule() {
    // Subscrive to Firebase documents
    this.firebaseService.subscribeDocument(`/user_scheduled_simulations/${this.user.uid}`).subscribe(async (res: any) => {

      let simulations = Object.keys(res).map((key) => {
        return {key, ...res[key]};
      })
        .filter((a) => (a.status !== 'done')).filter((a) => (!a.rescheduled))
        .sort((a, b) => {
          if (a.date < b.date) return -1; else if (b.date <= a.date) return 1;
        });

      await this.storage.set('scheduled-simulations-all', simulations); // Save from DB to localStorage
      this.events.publishData({'scheduled-simulations-all': true});

      let simulation = await this.storage.get('scheduled-simulation'); // We have this info if the user has been in simulations-peers page and chose a simulation to practice
      if (!simulation) return;

      // Get the updated data of the current schedule simulation from DB
      let updatedSimulation = res.find((s) => s.key == simulation.key);

      // FOR TESTING when manually deleting the simulations from the DB and it still exists in localstorage
      if (!simulations.length || !updatedSimulation) {
        this.storage.remove('scheduled-simulation');
        return;
      }

      // Notify status of the users
      if (!simulation.peerEntered && updatedSimulation.peerEntered === true) {
        this.msg2 = this.translate.instant(this.msg2);
        let msg = `${simulation.peerName}, ${this.msg2}`;
        this.presentToast(msg);
      } else if (updatedSimulation.status_peer == 'is-listening-first-simulation') {
        this.msg1 = this.translate.instant(this.msg1);
        let msg = `${simulation.peerName} ${this.msg1}`;
        this.presentToast(msg);
      } else if (updatedSimulation.status_peer && updatedSimulation.status_peer.includes('is-waiting')) {
        this.msg3 = this.translate.instant(this.msg3);
        let msg = `${simulation.peerName} ${this.msg3}`;
        this.presentToast(msg);
      }

      // the simulation was ended by the user with the manager role, so we need to finish it to the other user
      if (!simulation.firstSimulationDone && updatedSimulation.firstSimulationDone === true) this.events.publishData({'finish-simulation': true});
      else if (!simulation.secondSimulationDone && updatedSimulation.secondSimulationDone === true) this.events.publishData({'finish-simulation': true});

      // Copy values from the updated document in the DB and save in localstorage
      Object.keys(updatedSimulation).forEach((k) => {
        if (simulation[k] != updatedSimulation[k]) simulation[k] = updatedSimulation[k];
      });

      await this.storage.set('scheduled-simulation', simulation); // Save from DB to localStorage
      this.events.publishData({'scheduled-simulation': true});
    });
  }

  validateJourneyDemo() {
    this.authService.getUserJourney(this.user.companyLowercase, this.user.uid).then((res: any) => {
      if (!res) this.openExpiredDemoTimeModal()
      else {
        this.storage.get('user_journey').then(res => {
          if (res && res.journey && this.guideVisit && this.url != '/simulation-peers-add') this.storage.set('guided_visit', true)
        });
      }
    });
  }

  async openExpiredDemoTimeModal() {
    const popover = await this.modalController.create({
      component: GuidedVisitModalComponent,
      cssClass: 'guided-visit-demo-modal-expired',
      backdropDismiss: false,
      keyboardClose: false,
      mode: 'ios',
      componentProps: {
        lastDemoModal: true,
        expiredDemoModal: true
      }
    });

    await popover.present();
  }

  hasUrl(page) {
    let url = this.router.url;
    if (page.url && page.uel == url) return 'selected';
    //////routerLinkActive="{{hasUrl(p)}}"
  }

  menuOnClick(module: ModuleEnum) {
    const page = this.appPages.find(x => x.module === module);
    if (!page.url) this.router.navigateByUrl(page.items[0].url);

    this.appPages.forEach((p: any) => {
      p.showItems = false;
    });

    if (page.items) {
      if (page.showItems) page.showItems = false;
      else page.showItems = true;
    }
  }

  async presentToast(message) {
    const toast = await this.toastController.create({
      message,
      duration: 8000,
      position: 'bottom',
      cssClass: 'popup-slide',
      icon: 'checkmark-circle-outline',
      mode: 'ios',
      animated: true,
    });

    await toast.present();
  }

  private getSelectedModule = () => {
    const urlFragments = this.router.url.split('/');
    return this.availableModules.find(x => x === urlFragments[1]) || ModuleEnum.feedback;
  };

  private listenUserJourney(uid: string, companyKey: string): void {
    this.firebaseService.valueChanges(`user_journey/${companyKey}/${uid}`).subscribe(userJourney => {
      this.userJourney = userJourney;
      const menuItems = this.appPages.map(menu => {
        return {
          ...menu,
          status: userJourney?.modules?.[menu.module]?.status
        };
      });
      this.appPages = [
        ...menuItems.filter(x => x.status === 'active'),
        ...menuItems.filter(x => x.status !== 'active')
      ];
    });
  }

  private initMenuListener(): void {
    this.events.initEvent('open-menu');
    this.events.getEvent('open-menu').subscribe((opened) => {
      if(opened) {
        this.mainMenu.open().then();
      } else {
        this.mainMenu.close().then();
      }
    });
  }

  private initActiveMenuListener(): void {
    this.events.initEvent('open-menu-module');
    this.events.getEvent('open-menu-module').subscribe(module => {
      const auxModule = this.getSelectedModule();
      this.menuOnClick(module || auxModule);
    });
  }

  private listenAuthState(): void {
    this.authService.afAuth.authState.subscribe(async (authUser) => {
      if (authUser && authUser?.emailVerified) {
        const user: any = await this.firebaseService.getDocument(`/users/${authUser.uid}`);
        let companyId = user.companyId;
        if (!companyId && user.company) {
          companyId = (await this.firebaseService.getDocumentByIndexData(`companies`, 'name_lowercase', user.company)).id;
        }
        this.appPages = await this.settingsService.getMenuSettings(this.appPages, companyId);
        this.listenUserJourney(authUser.uid, user.companyLowercase);
      }
    });
  }
}
